/**
 * @file OutlineBubble.tsx
 * @brief Outline chat bubble + gapped avatar ring — web port of the mobile
 *        transcript bubble.
 *
 * @details
 * Same shape and numbers as FRONTEND `VZUIKit/VZOutlineBubble.tsx` (DES-approved
 * 27-09-2026): an OUTLINE only, no fill, in the speaker's colour; the name sits
 * in a break in the top line, the time in a break in the bottom line (legend
 * style); a tail at the avatar-side bottom corner that swoops into a rounded
 * tip, with a dot below it; the avatar sits outside in a ring whose opening
 * aims at that dot.
 *
 * Built like the mobile one, with no measuring: corners are r×r boxes with two
 * borders and a radius (true quarter-circles), straight edges are plain boxes,
 * the top/bottom edges are flex rows with the label IN the row so the line
 * stops either side of it, and the tail + dot is one fixed-size SVG.
 *
 * ⚠️ Pieces meet end to end, never overlapping: the grey is translucent, and
 * an overlap shows as a darker spot. Don't "tidy" the numbers; they are DES's.
 *
 * @references
 * - FRONTEND/src/shared/components/VZUIKit/VZOutlineBubble.tsx
 * - features/company-meetings/components/MeetingDetailView.tsx (Transcript)
 * - features/sessions/components/SessionDetailDialog.tsx (Transcript)
 *
 * @author DES
 * @date 30-09-2026
 */

import type { CSSProperties, ReactNode } from "react";
import { cn } from "@/shared/theme/cn";
import {
  BUBBLE_STROKE,
  RADIUS,
  TAIL_SPAN,
  SVG_PAD,
  TAIL_SPACE,
  LABEL_PAD,
  LABEL_INSET,
  LABEL_HALF,
  PLAIN_GAP,
  S,
  TAIL_SVG_W,
  TAIL_SVG_H,
  TAIL_PATH_START,
  TAIL_PATH_END,
  TAIL_DOT_Y,
  DOT_R,
} from "./outlineBubbleGeometry";

type Side = "start" | "end";

const line: CSSProperties = { backgroundColor: "var(--bubble)" };
const edge: CSSProperties = { borderColor: "var(--bubble)" };

// `pointer-events-auto`: the edge it sits in is `pointer-events-none` (so the
// outline never eats clicks), which also swallowed a label's buttons, e.g. the
// transcript play button (DES 01-10-2026).
function Label({ children }: { children: ReactNode }) {
  return (
    <span
      className="pointer-events-auto flex shrink-0 items-center whitespace-nowrap"
      style={{ height: LABEL_HALF * 2, padding: `0 ${LABEL_PAD}px` }}
    >
      {children}
    </span>
  );
}

export function OutlineBubble({
  side,
  labelSide,
  className,
  topLabel,
  bottomLabel,
  children,
}: {
  side: Side;
  /**
   * Which side the name and time sit on; defaults to `side`. An RTL line
   * whose tail stays on the start side puts its name on the right
   * (20-10-2026, the evidence timeline).
   */
  labelSide?: Side;
  /** Padding etc. for the bubble box; the colour comes from `--bubble`. */
  className?: string;
  /** Drawn in the top break (the speaker's name). */
  topLabel?: ReactNode;
  /** Drawn in the bottom break (the time). */
  bottomLabel?: ReactNode;
  children: ReactNode;
}) {
  const start = side === "start";
  const labelsStart = (labelSide ?? side) === "start";

  return (
    <div
      className="min-w-0 max-w-full"
      style={{
        paddingBottom: TAIL_SPACE,
        paddingTop: topLabel ? LABEL_HALF : 0,
      }}
    >
      <div className={cn("relative w-fit max-w-full", className)}>
        {/* Width floors: zero-high copies of the labels plus the corners
            around them, so the bubble is never too narrow for its labels. */}
        {topLabel ? (
          <div
            aria-hidden
            className="h-0 w-fit overflow-hidden whitespace-nowrap opacity-0"
            style={{
              paddingLeft: RADIUS + LABEL_INSET + LABEL_PAD,
              paddingRight: LABEL_PAD + RADIUS + 8,
            }}
          >
            {topLabel}
          </div>
        ) : null}
        {bottomLabel ? (
          <div
            aria-hidden
            className="h-0 w-fit overflow-hidden whitespace-nowrap opacity-0"
            style={{
              paddingLeft: RADIUS + LABEL_INSET + LABEL_PAD,
              paddingRight: LABEL_PAD + TAIL_SPAN + 12,
            }}
          >
            {bottomLabel}
          </div>
        ) : null}

        {children}

        {/* ── The outline ─────────────────────────────────────── */}
        <span
          aria-hidden
          className="pointer-events-none absolute left-0 top-0"
          style={{
            ...edge,
            width: RADIUS,
            height: RADIUS,
            borderTopWidth: BUBBLE_STROKE,
            borderLeftWidth: BUBBLE_STROKE,
            borderTopLeftRadius: RADIUS,
          }}
        />
        <span
          aria-hidden
          className="pointer-events-none absolute right-0 top-0"
          style={{
            ...edge,
            width: RADIUS,
            height: RADIUS,
            borderTopWidth: BUBBLE_STROKE,
            borderRightWidth: BUBBLE_STROKE,
            borderTopRightRadius: RADIUS,
          }}
        />
        {/* The avatar-side bottom corner has none: that edge runs on into
            the tail. */}
        <span
          aria-hidden
          className={cn(
            "pointer-events-none absolute bottom-0",
            start ? "right-0" : "left-0",
          )}
          style={
            start
              ? {
                  ...edge,
                  width: RADIUS,
                  height: RADIUS,
                  borderBottomWidth: BUBBLE_STROKE,
                  borderRightWidth: BUBBLE_STROKE,
                  borderBottomRightRadius: RADIUS,
                }
              : {
                  ...edge,
                  width: RADIUS,
                  height: RADIUS,
                  borderBottomWidth: BUBBLE_STROKE,
                  borderLeftWidth: BUBBLE_STROKE,
                  borderBottomLeftRadius: RADIUS,
                }
          }
        />
        {/* Side edges: the avatar side runs down to the bottom border, where
            the tail's SVG continues it. */}
        <span
          aria-hidden
          className="pointer-events-none absolute left-0"
          style={{
            ...line,
            top: RADIUS,
            width: BUBBLE_STROKE,
            bottom: start ? BUBBLE_STROKE : RADIUS,
          }}
        />
        <span
          aria-hidden
          className="pointer-events-none absolute right-0"
          style={{
            ...line,
            top: RADIUS,
            width: BUBBLE_STROKE,
            bottom: start ? RADIUS : BUBBLE_STROKE,
          }}
        />

        {/* Top edge: short run, name break, long run. Mirrored for 'end'. */}
        <span
          aria-hidden={!topLabel}
          className={cn(
            "pointer-events-none absolute top-0 flex items-center",
            !labelsStart && "flex-row-reverse",
          )}
          style={{ left: RADIUS, right: RADIUS, height: BUBBLE_STROKE }}
        >
          <span
            className="shrink-0"
            style={{ ...line, width: LABEL_INSET, height: BUBBLE_STROKE }}
          />
          {topLabel ? (
            <Label>{topLabel}</Label>
          ) : (
            <span className="shrink-0" style={{ width: PLAIN_GAP }} />
          )}
          <span
            className="min-w-0 flex-1"
            style={{ ...line, height: BUBBLE_STROKE }}
          />
        </span>

        {/* Bottom edge: from the tail's swoop to the far corner. */}
        <span
          aria-hidden={!bottomLabel}
          className={cn(
            "pointer-events-none absolute bottom-0 flex items-center",
            !labelsStart && "flex-row-reverse",
          )}
          style={{
            height: BUBBLE_STROKE,
            left: start ? TAIL_SPAN + S : RADIUS,
            right: start ? RADIUS : TAIL_SPAN + S,
          }}
        >
          <span
            className="min-w-0 flex-1"
            style={{ ...line, height: BUBBLE_STROKE }}
          />
          {bottomLabel ? (
            <Label>{bottomLabel}</Label>
          ) : (
            <span className="shrink-0" style={{ width: PLAIN_GAP }} />
          )}
          <span
            className="shrink-0"
            style={{ ...line, width: LABEL_INSET, height: BUBBLE_STROKE }}
          />
        </span>

        <svg
          aria-hidden
          width={TAIL_SVG_W}
          height={TAIL_SVG_H}
          className="pointer-events-none absolute overflow-visible"
          style={{
            bottom: -(TAIL_SVG_H - BUBBLE_STROKE),
            ...(start ? { left: -SVG_PAD } : { right: -SVG_PAD }),
          }}
        >
          <path
            d={start ? TAIL_PATH_START : TAIL_PATH_END}
            stroke="var(--bubble)"
            strokeWidth={BUBBLE_STROKE}
            strokeLinecap="butt"
            strokeLinejoin="round"
            fill="none"
          />
          <circle
            cx={start ? SVG_PAD + S : TAIL_SVG_W - SVG_PAD - S}
            cy={TAIL_DOT_Y}
            r={DOT_R}
            fill="var(--bubble)"
          />
        </svg>
      </div>
    </div>
  );
}

/** A thin ring around an avatar, open on the side facing the bubble. */
export function AvatarRing({
  size,
  side,
  gapAngle = 0,
  children,
}: {
  size: number;
  side: Side;
  gapAngle?: number;
  children: ReactNode;
}) {
  const c = size / 2;
  const r = c - BUBBLE_STROKE / 2;
  const gap = 70;
  const centre = side === "start" ? gapAngle : 180 - gapAngle;
  const toXY = (deg: number) => {
    const rad = (deg * Math.PI) / 180;
    return `${c + r * Math.cos(rad)},${c + r * Math.sin(rad)}`;
  };
  const d = `M${toXY(centre + gap / 2)} A${r},${r} 0 1 1 ${toXY(centre - gap / 2 + 360)}`;

  return (
    <span
      className="relative inline-flex shrink-0 items-center justify-center"
      style={{ width: size, height: size }}
    >
      <svg
        aria-hidden
        width={size}
        height={size}
        className="pointer-events-none absolute inset-0"
      >
        <path
          d={d}
          stroke="var(--bubble)"
          strokeWidth={BUBBLE_STROKE}
          strokeLinecap="round"
          fill="none"
        />
      </svg>
      {children}
    </span>
  );
}
